import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiService } from '../../../../core/services/api.service';

// ── Dashboard ──────────────────────────────────────────────────────────────
export interface DashboardStats {
  totalProperties: number;
  activeContracts: number;
  pendingPayments: number;
  upcomingAppointments: number;
}

export interface DashboardResponse {
  message: string;
  data: DashboardStats;
}

// ── My Properties ──────────────────────────────────────────────────────────
export interface LocalizedText {
  en?: string;
  ar?: string;
}

export interface PortalPropertyUnit {
  _id?: string;
  id?: string;
  title_ar?: string;
  title_en?: string;
  gallery?: Array<{
    secure_url?: string;
    public_id?: string;
    _id?: string;
    id?: string;
  }>;
  location?: {
    country?: LocalizedText;
    city?: LocalizedText;
    area?: LocalizedText;
    address?: LocalizedText;
  };
}

export interface OwnedPortalProperty {
  dealId: string;
  status: string;
  targetId: string | null;
  targetType: string;
  finalPrice: number;
  currency: string;
  paymentMethod: string;
  purchaseDate: string;
}

export interface RentedPortalProperty {
  contractId: string;
  status: string;
  unit: PortalPropertyUnit | null;
  rentAmount: number;
  startDate: string;
  endDate: string;
  paymentFrequency: string;
}

export interface MyPropertiesResponse {
  owned: OwnedPortalProperty[];
  rented: RentedPortalProperty[];
  summary: {
    totalOwned: number;
    totalRented: number;
  };
}

// ── My Contracts ───────────────────────────────────────────────────────────
export interface MyContract {
  _id: string;
  contractNumber: string;
  contractType: string;
  propertyTitle: string;
  propertyId: string;
  startDate: string;
  endDate: string;
  amount: number;
  status: string;
  paymentFrequency?: string;
  landlordName?: string;
  contractDocument?: string;
}

export interface MyContractsResponse {
  message: string;
  contracts: MyContract[];
}

// ── My Installments ────────────────────────────────────────────────────────
export interface Installment {
  _id: string;
  installmentType: string;
  installmentNumber: number;
  amount: number;
  status: string;
  dealId: string;
  payment?: {
    _id: string;
    amount: number;
    currency: string;
    method: string;
    status: string;
    receiptImage?: {
      secure_url: string;
      public_id: string;
    };
    createdAt: string;
    updatedAt: string;
  } | null;
}

export interface MyInstallmentsResponse {
  installments: Installment[];
  summary: {
    totalInstallments: number;
    paidCount: number;
    upcomingCount: number;
    totalPaid: number;
    overdueCount: number;
    totalRemaining: number;
    nextDueDate: string | null;
    nextDueAmount: number;
  };
}

// ── Construction Progress ──────────────────────────────────────────────────
export interface ConstructionUpdate {
  _id: string;
  projectTitle: string;
  projectId: string;
  updateDate: string;
  progressPercentage: number;
  phase: string;
  description: string;
  images: string[];
  estimatedCompletion: string;
}

export interface ConstructionProgressResponse {
  message: string;
  updates: ConstructionUpdate[];
}

// ── My Tickets ─────────────────────────────────────────────────────────────
export interface Ticket {
  _id: string;
  ticketNumber: string;
  subject: string;
  category: string;
  priority: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  propertyTitle?: string;
  propertyId?: string;
  lastReply?: string;
}

export interface MyTicketsResponse {
  message: string;
  tickets: Ticket[];
}

@Injectable({ providedIn: 'root' })
export class PortalService {
  private api = inject(ApiService);

  getDashboard(): Observable<DashboardResponse> {
    return this.api.get<DashboardResponse>('/portal/dashboard');
  }

  getMyProperties(): Observable<MyPropertiesResponse> {
    return this.api.get<MyPropertiesResponse>('/portal/my-properties');
  }

  getMyContracts(): Observable<MyContractsResponse> {
    return this.api.get<MyContractsResponse>('/portal/my-contracts');
  }

  getMyInstallments(): Observable<MyInstallmentsResponse> {
    return this.api.get<MyInstallmentsResponse>('/portal/my-installments');
  }

  getConstructionProgress(): Observable<ConstructionProgressResponse> {
    return this.api.get<ConstructionProgressResponse>('/portal/construction-progress');
  }

  getMyTickets(): Observable<MyTicketsResponse> {
    return this.api.get<MyTicketsResponse>('/portal/my-tickets');
  }
}
